import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { FindingIn, Issue, ItemDetail, Severity, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { plain } from "../../lib/commentText.ts";
import { spaceApi } from "../../lib/paths.ts";
import { useAction } from "../../lib/resource.ts";
import { me } from "../../lib/state.ts";
import { toast } from "../../lib/toast.ts";
import { Button } from "../../ui/Button.tsx";
import { DialogActions } from "../../ui/Dialog.tsx";
import { Field, Form, Select } from "../../ui/form.tsx";
import { DismissedIcon, ResolvedIcon } from "../../ui/icons.tsx";
import { Segmented } from "../../ui/Segmented.tsx";
import { SeverityPicker } from "../../ui/SeverityPicker.tsx";
import { MentionTextarea, RichText } from "../comments/richText.tsx";

/** Nobody in particular to suggest first (the item's links are). */
const NOBODY: ReadonlySet<string> = new Set();

/** A finding being written: what it'll send, and an identity of its own while it's edited. */
type Draft = FindingIn & { uid: number };
let uids = 0;
const uid = () => {
  uids += 1;
  return uids;
};

const findingOf = (i: Issue): Draft => ({
  uid: uid(),
  severity: i.severity,
  message: i.message,
  path: i.path ?? null,
  rule: i.rule ?? null,
});

interface RowProps {
  space: SpaceOut;
  n: number;
  f: FindingIn;
  paths: readonly string[];
  item: ItemDetail;
  onChange: (f: FindingIn) => void;
  onRemove: () => void;
}

function FindingRow({ space, n, f, paths, item, onChange, onRemove }: RowProps) {
  const none = { value: "", label: "—" };
  return (
    <fieldset class="grid gap-2 rounded-md border border-zinc-200 p-3 sm:grid-cols-2">
      <legend class="px-1 text-xs font-medium text-zinc-500">{t("review.finding", { n })}</legend>
      <Field label={t("review.severity")} group={true}>
        <SeverityPicker
          label={t("review.severity")}
          value={f.severity}
          onValue={(s) => onChange({ ...f, severity: s })}
          class="self-start"
        />
      </Field>
      <Field label={t("review.path")}>
        <Select
          value={f.path ?? ""}
          onValue={(v) => onChange({ ...f, path: v || null })}
          options={[none, ...paths.map((p) => ({ value: p, label: p }))]}
        />
      </Field>
      <div class="sm:col-span-2">
        <Field label={t("review.message")}>
          <MentionTextarea
            space={space}
            value={f.message}
            onValue={(v) => onChange({ ...f, message: v })}
            about={{ item: item.id, people: NOBODY }}
            rows={2}
          />
        </Field>
      </div>
      <Field label={t("review.rule")}>
        <Select
          value={f.rule ?? ""}
          onValue={(v) => onChange({ ...f, rule: v || null })}
          options={[none, ...item.rules.map((r) => ({ value: r.id, label: r.name }))]}
        />
      </Field>
      <Button variant="ghost" size="sm" class="self-end" onClick={onRemove}>
        {t("review.removeFinding")}
      </Button>
    </fieldset>
  );
}

/** What to do with another reviewer's finding: another severity, and/or closing it (as fixed, or as wrong). */
interface Decision {
  severity?: Severity | undefined;
  close?: "resolve" | "dismiss" | undefined;
}

/**
 * Others' findings, each with who found it and what to do with it: its severity as chips (another one adjusts it),
 * and closing it as resolved (it was right, and it's fixed) or dismissed (it was wrong or irrelevant).
 */
function Others({
  space,
  others,
  decisions,
  onChange,
}: {
  space: SpaceOut;
  others: Issue[];
  decisions: Record<string, Decision>;
  onChange: (id: string, d: Decision) => void;
}) {
  return others.length > 0 ? (
    <fieldset class="flex flex-col gap-2">
      <legend class="mb-1 text-sm font-medium text-zinc-800">{t("review.others")}</legend>
      {others.map((i) => {
        const id = i.id ?? "";
        const d = decisions[id] ?? {};
        const severity = d.severity ?? i.severity;
        const closing = (c: "resolve" | "dismiss") => onChange(id, { ...d, close: d.close === c ? undefined : c });
        return (
          <div key={id} class="flex flex-col gap-2 rounded-md border border-zinc-200 p-3">
            <span class="text-xs text-zinc-500">{i.reviewer_name ?? t("issues.reviewer")}</span>
            <RichText
              space={space}
              text={i.message}
              class={`text-sm ${d.close ? "text-zinc-400 line-through" : "text-zinc-800"}`}
            />
            <div class="flex flex-wrap items-center justify-between gap-2">
              <SeverityPicker
                value={severity}
                label={t("review.decision", { message: plain(i.message) })}
                onValue={(s) => onChange(id, { ...d, severity: s === i.severity ? undefined : s })}
              />
              <Segmented
                label={t("review.close", { message: plain(i.message) })}
                size="xs"
                value={d.close}
                onValue={closing}
                options={[
                  { value: "resolve", label: t("review.resolved"), tone: "green", icon: <ResolvedIcon size={14} /> },
                  { value: "dismiss", label: t("review.dismissed"), icon: <DismissedIcon size={14} /> },
                ]}
              />
            </div>
          </div>
        );
      })}
    </fieldset>
  ) : null;
}

/** The review to send: my findings (blank ones dropped), what to do with others', and the comment. */
function reviewOf(item: ItemDetail, findings: readonly Draft[], decisions: Record<string, Decision>, message: string) {
  const closing = (c: Decision["close"]) => Object.keys(decisions).filter((id) => decisions[id]?.close === c);
  return {
    item: item.id,
    issues: findings.filter((f) => f.message.trim() !== "").map(({ uid: _, ...f }) => f),
    resolve: closing("resolve"),
    dismiss: closing("dismiss"),
    adjust: Object.fromEntries(Object.entries(decisions).flatMap(([id, d]) => (d.severity ? [[id, d.severity]] : []))),
    message: message.trim() || null,
    as_of: item.head,
  };
}

/** My findings being written: each kept by its identity as others are added and removed. */
function useFindings(initial: () => Draft[]) {
  const [findings, setFindings] = useState(initial);
  return {
    findings,
    add: () => setFindings([...findings, { uid: uid(), severity: "error", message: "" }]),
    change: (u: number, next: FindingIn) => setFindings(findings.map((x) => (x.uid === u ? { ...next, uid: u } : x))),
    remove: (u: number) => setFindings(findings.filter((x) => x.uid !== u)),
  };
}

/** My complete set of findings (pre-filled with my open ones; blank ones are dropped), what to do with others'
 *  (keep, adjust, close), and a comment. */
export function ReviewForm(p: { space: SpaceOut; item: ItemDetail; onDone: () => void; onCancel: () => void }) {
  const { space, item, onDone } = p;
  const mine = me.value?.id;
  const byReviewers = item.issues.filter((i) => i.source === "review");
  const { findings, add, change, remove } = useFindings(() =>
    byReviewers.filter((i) => i.reviewer === mine).map(findingOf),
  );
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [message, setMessage] = useState("");
  const [submit, busy] = useAction(async () => {
    await api.post(spaceApi(space, "/reviews"), reviewOf(item, findings, decisions, message));
    toast(t("review.saved"));
    onDone();
  });
  const keys = [...(item.schema?.fields ?? []).map((f) => f.key), ...Object.keys(item.data ?? {})];
  const paths = [...new Set(keys)].map((k) => `data.${k}`);
  return (
    <Form class="flex flex-col gap-4" onSubmit={submit}>
      <p class="text-sm text-zinc-600">{t("review.hint")}</p>
      {findings.map((f, i) => (
        <FindingRow
          key={f.uid}
          space={space}
          n={i + 1}
          f={f}
          paths={paths}
          item={item}
          onChange={(next) => change(f.uid, next)}
          onRemove={() => remove(f.uid)}
        />
      ))}
      <Button class="self-start" onClick={add}>
        {t("review.addFinding")}
      </Button>
      <Others
        space={space}
        others={byReviewers.filter((i) => i.reviewer !== mine && i.id)}
        decisions={decisions}
        onChange={(id, d) => setDecisions({ ...decisions, [id]: d })}
      />
      <Field label={t("review.comment")}>
        <MentionTextarea
          space={space}
          value={message}
          onValue={setMessage}
          about={{ item: item.id, people: NOBODY }}
          rows={3}
        />
      </Field>
      <DialogActions>
        <Button answer="cancel" onClick={p.onCancel}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} busy={busy}>
          {findings.length === 0 ? t("review.submitClear") : t("review.submit")}
        </Button>
      </DialogActions>
    </Form>
  );
}
